import * as THREE from 'three';
import { Obstacle, ObstacleType } from './Obstacle';
import { randomRange, randomInt } from '../utils/helpers';
import { LANE_WIDTH, WORLD_CONFIG } from './constants';

/**
 * Game world with ground, decorations, and obstacle management
 * Includes anti-softlock logic to prevent impossible patterns
 */
export class World {
  public group: THREE.Group;
  public obstacles: Obstacle[] = [];

  private groundTiles: THREE.Mesh[] = [];
  private decorations: THREE.Mesh[] = [];

  private nextObstacleZ = WORLD_CONFIG.INITIAL_OBSTACLE_Z;
  private minObstacleGap = 12;
  private maxObstacleGap = 22;

  // Anti-softlock: track previous pattern's safe lanes
  private lastSafeLanes: number[] = [0, 1, 2]; // All lanes safe initially
  private consecutiveDifficultPatterns = 0;

  constructor(scene: THREE.Scene) {
    this.group = new THREE.Group();
    scene.add(this.group);

    this.createGround();
    this.createLaneMarkers();
    this.createBackgroundDecorations();
  }

  // ===========================================================================
  // Public Methods
  // ===========================================================================

  public update(deltaTime: number, speed: number, distance: number): void {
    this.updateGroundTiles(deltaTime, speed);
    this.updateObstacles(deltaTime, speed);
    this.spawnObstacles(distance, speed);
    this.updateDecorations(deltaTime, speed);
  }

  public reset(): void {
    for (const obstacle of this.obstacles) {
      this.group.remove(obstacle.mesh);
    }
    this.obstacles = [];

    for (let i = 0; i < this.groundTiles.length; i++) {
      this.groundTiles[i].position.z = i * WORLD_CONFIG.TILE_LENGTH;
    }

    this.nextObstacleZ = WORLD_CONFIG.INITIAL_OBSTACLE_Z;
    this.lastSafeLanes = [0, 1, 2];
    this.consecutiveDifficultPatterns = 0;

    this.resetDecorations();
  }

  public setObstacleGap(min: number, max: number): void {
    this.minObstacleGap = min;
    this.maxObstacleGap = max;
  }

  // ===========================================================================
  // Ground Creation
  // ===========================================================================

  private createGround(): void {
    const texture = this.createGridTexture();
    const material = new THREE.MeshLambertMaterial({ map: texture, color: 0x4a7c8c });
    const geometry = new THREE.PlaneGeometry(WORLD_CONFIG.GROUND_WIDTH, WORLD_CONFIG.TILE_LENGTH);

    for (let i = 0; i < WORLD_CONFIG.TILE_COUNT; i++) {
      const tile = new THREE.Mesh(geometry, material);
      tile.rotation.x = -Math.PI / 2;
      tile.position.z = i * WORLD_CONFIG.TILE_LENGTH;
      tile.receiveShadow = true;
      this.groundTiles.push(tile);
      this.group.add(tile);
    }
  }

  private createGridTexture(): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    ctx.fillStyle = '#2d4a5e';
    ctx.fillRect(0, 0, 256, 256);

    ctx.strokeStyle = '#3d5a6e';
    ctx.lineWidth = 2;

    const gridSize = 32;
    for (let i = 0; i <= 256; i += gridSize) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, 256);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(256, i);
      ctx.stroke();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(4, 8);

    return texture;
  }

  private createLaneMarkers(): void {
    const material = new THREE.MeshLambertMaterial({
      color: 0x7ec8e3,
      emissive: 0x7ec8e3,
      emissiveIntensity: 0.2,
    });

    for (let lane = -1; lane <= 1; lane += 2) {
      const geometry = new THREE.BoxGeometry(0.1, 0.02, 100);
      const line = new THREE.Mesh(geometry, material);
      line.position.set(lane * LANE_WIDTH * 0.5 + lane * 0.75, 0.01, 50);
      this.group.add(line);
    }
  }

  private createBackgroundDecorations(): void {
    const material = new THREE.MeshLambertMaterial({ color: 0x1a3040, flatShading: true });

    for (let side = -1; side <= 1; side += 2) {
      for (let i = 0; i < 10; i++) {
        const height = randomRange(3, 8);
        const geometry = new THREE.BoxGeometry(1, height, 1);
        const pillar = new THREE.Mesh(geometry, material);
        pillar.position.set(
          side * (10 + randomRange(0, 3)),
          height / 2,
          i * 15 + randomRange(-3, 3)
        );
        this.decorations.push(pillar);
        this.group.add(pillar);
      }
    }
  }

  // ===========================================================================
  // Update Methods
  // ===========================================================================

  private updateGroundTiles(deltaTime: number, speed: number): void {
    for (const tile of this.groundTiles) {
      tile.position.z -= speed * deltaTime;

      if (tile.position.z < -WORLD_CONFIG.TILE_LENGTH) {
        tile.position.z += WORLD_CONFIG.TILE_LENGTH * WORLD_CONFIG.TILE_COUNT;
      }
    }
  }

  private updateObstacles(deltaTime: number, speed: number): void {
    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      const obstacle = this.obstacles[i];
      obstacle.update(speed, deltaTime);

      if (obstacle.isOffScreen()) {
        this.group.remove(obstacle.mesh);
        this.obstacles.splice(i, 1);
      }
    }
  }

  private updateDecorations(deltaTime: number, speed: number): void {
    for (const deco of this.decorations) {
      deco.position.z -= speed * deltaTime;
      if (deco.position.z < -20) {
        deco.position.z += 150;
      }
    }
  }

  private resetDecorations(): void {
    const halfCount = this.decorations.length / 2;
    for (let i = 0; i < this.decorations.length; i++) {
      const idx = i % halfCount;
      this.decorations[i].position.z = idx * 15 + randomRange(-3, 3);
    }
  }

  // ===========================================================================
  // Obstacle Spawning with Anti-Softlock Logic
  // ===========================================================================

  private spawnObstacles(distance: number, speed: number): void {
    const spawnZ = Math.max(80, 100 - speed * 2);

    if (distance > this.nextObstacleZ - spawnZ) {
      this.spawnSafePattern();
      this.calculateNextSpawnDistance(speed);
    }
  }

  /**
   * Spawn a pattern that is guaranteed to be survivable
   * based on the player's possible positions from the last pattern
   */
  private spawnSafePattern(): void {
    // If too many consecutive difficult patterns, force an easy one
    if (this.consecutiveDifficultPatterns >= 2) {
      this.spawnSingleObstacle();
      this.consecutiveDifficultPatterns = 0;
      return;
    }

    // Choose pattern type
    const patternType = randomInt(0, 3);

    switch (patternType) {
      case 0:
        this.spawnSingleObstacle();
        break;
      case 1:
        this.spawnSafeDoubleObstacle();
        break;
      case 2:
        this.spawnSafeLowRow();
        break;
      default:
        this.spawnSingleObstacle();
    }
  }

  private spawnSingleObstacle(): void {
    // Single obstacle: 2 lanes are always safe
    const blockedLane = randomInt(0, 2);
    const safeLanes = [0, 1, 2].filter(l => l !== blockedLane);
    
    // Prefer blocking a lane the player can escape from
    const canEscape = safeLanes.some(l => this.lastSafeLanes.includes(l));
    
    if (!canEscape) {
      // Force block a different lane to ensure escape route
      const forcedBlockLane = this.lastSafeLanes[randomInt(0, this.lastSafeLanes.length - 1)];
      const actualBlockLane = [0, 1, 2].find(l => l !== forcedBlockLane) ?? 1;
      
      const types: ObstacleType[] = ['block', 'spike', 'low'];
      const obstacle = new Obstacle(types[randomInt(0, 2)], actualBlockLane, 80);
      this.obstacles.push(obstacle);
      this.group.add(obstacle.mesh);
      
      this.lastSafeLanes = [0, 1, 2].filter(l => l !== actualBlockLane);
    } else {
      const types: ObstacleType[] = ['block', 'spike', 'low'];
      const obstacle = new Obstacle(types[randomInt(0, 2)], blockedLane, 80);
      this.obstacles.push(obstacle);
      this.group.add(obstacle.mesh);
      
      this.lastSafeLanes = safeLanes;
    }

    this.consecutiveDifficultPatterns = 0;
  }

  private spawnSafeDoubleObstacle(): void {
    // Double obstacle: only 1 lane is safe
    // Must ensure the safe lane overlaps with previous safe lanes
    
    // Find a lane that was safe before to keep safe now
    const mustKeepSafe = this.lastSafeLanes[randomInt(0, this.lastSafeLanes.length - 1)];
    const blockedLanes = [0, 1, 2].filter(l => l !== mustKeepSafe);
    
    // Use all obstacle types including wall
    const types: ObstacleType[] = ['block', 'spike', 'wall', 'low'];

    const obs1 = new Obstacle(types[randomInt(0, 2)], blockedLanes[0], 80);
    const obs2 = new Obstacle(types[randomInt(0, 2)], blockedLanes[1], 80);

    this.obstacles.push(obs1, obs2);
    this.group.add(obs1.mesh, obs2.mesh);

    this.lastSafeLanes = [mustKeepSafe];
    this.consecutiveDifficultPatterns++;
  }

  private spawnSafeLowRow(): void {
    // Low row: 2 lanes blocked with low obstacles (jumpable), 1 lane open
    // The open lane must be reachable from previous safe lanes
    
    // Prefer keeping a lane that was safe before
    let skipLane: number;
    
    if (this.lastSafeLanes.length === 1) {
      // Only one safe lane before - must keep it or an adjacent one
      const prevSafe = this.lastSafeLanes[0];
      const options = [prevSafe];
      if (prevSafe > 0) options.push(prevSafe - 1);
      if (prevSafe < 2) options.push(prevSafe + 1);
      skipLane = options[randomInt(0, options.length - 1)];
    } else {
      // Multiple safe lanes - any overlapping one works
      skipLane = this.lastSafeLanes[randomInt(0, this.lastSafeLanes.length - 1)];
    }

    for (let lane = 0; lane < 3; lane++) {
      if (lane !== skipLane) {
        const obs = new Obstacle('low', lane, 80);
        this.obstacles.push(obs);
        this.group.add(obs.mesh);
      }
    }

    // Safe lanes: the skip lane OR any lane (if player jumps)
    // For safety tracking, we consider only the guaranteed safe path
    this.lastSafeLanes = [skipLane];
    this.consecutiveDifficultPatterns++;
  }

  private calculateNextSpawnDistance(speed: number): void {
    let gap = randomRange(this.minObstacleGap, this.maxObstacleGap);
    
    // If only one lane is safe, add extra spacing for lane change time
    if (this.lastSafeLanes.length === 1) {
      gap += 3; // Extra buffer for difficult patterns
    }
    
    // If consecutive difficult patterns, add more spacing
    if (this.consecutiveDifficultPatterns >= 1) {
      gap += 2;
    }
    
    const speedFactor = Math.max(0.7, 1 - speed / 150);
    this.nextObstacleZ += gap * speedFactor;
  }
}
